import { 
  doc, 
  getDoc, 
  setDoc, 
  deleteDoc,
  collection, 
  query, 
  where, 
  getDocs, 
  writeBatch 
} from 'firebase/firestore';
import { auth, db } from '../config/firebase';
import { User as AppUser } from '../types';

export interface AccountMergeResult {
  success: boolean;
  message: string;
  mergedData?: {
    apiaries: number;
    hives: number;
    inspections: number;
  };
  error?: string;
}

export const findAccountsWithSameEmail = async (email: string): Promise<AppUser[]> => {
  try {
    const usersQuery = query(
      collection(db, 'users'),
      where('email', '==', email)
    );
    const existingUsers = await getDocs(usersQuery);
    
    return existingUsers.docs.map(doc => ({
      uid: doc.id,
      ...doc.data()
    } as AppUser));
  } catch (error) {
    console.error('Error finding accounts with same email:', error);
    return [];
  }
};

export const mergeAccountData = async (
  fromUserId: string, 
  toUserId: string
): Promise<AccountMergeResult> => {
  try {
    console.log(`🔄 Starting account merge from ${fromUserId} to ${toUserId}`);
    
    // Get the source user data
    const sourceUserDoc = await getDoc(doc(db, 'users', fromUserId));
    if (!sourceUserDoc.exists()) {
      return {
        success: false,
        message: 'Source user account not found',
        error: 'SOURCE_USER_NOT_FOUND'
      };
    }
    
    const sourceUserData = sourceUserDoc.data() as AppUser;
    
    // Get the target user data
    const targetUserDoc = await getDoc(doc(db, 'users', toUserId));
    if (!targetUserDoc.exists()) {
      return {
        success: false,
        message: 'Target user account not found',
        error: 'TARGET_USER_NOT_FOUND'
      };
    }
    
    const targetUserData = targetUserDoc.data() as AppUser;
    
    // Merge user profiles (keeping Google profile data but preserving company info, etc.)
    const mergedUser: AppUser = {
      ...sourceUserData,
      uid: toUserId, // Use the new Google UID
      name: targetUserData.name || sourceUserData.name || '',
      email: targetUserData.email || sourceUserData.email || '',
      profileImageUrl: targetUserData.profileImageUrl || sourceUserData.profileImageUrl,
      joined: sourceUserData.joined || new Date().toISOString(),
      // Keep all other existing data like company info, location, etc.
    };
    
    // Update the target user document with merged data
    await setDoc(doc(db, 'users', toUserId), mergedUser);
    console.log('✅ User profile merged successfully');
    
    // Find and update all related data
    const [apiaries, hives, inspections] = await Promise.all([
      getDocs(query(collection(db, 'apiaries'), where('user_id', '==', fromUserId))),
      getDocs(query(collection(db, 'hives'), where('user_id', '==', fromUserId))),
      getDocs(query(collection(db, 'inspections'), where('user_id', '==', fromUserId)))
    ]);
    
    console.log(`📊 Found data to migrate:`, {
      apiaries: apiaries.size,
      hives: hives.size,
      inspections: inspections.size
    });
    
    // Update all data in batches
    const batch = writeBatch(db);
    
    // Update apiaries
    apiaries.docs.forEach(docRef => {
      console.log(`🏠 Updating apiary: ${docRef.id}`);
      batch.update(docRef.ref, { user_id: toUserId });
    });
    
    // Update hives  
    hives.docs.forEach(docRef => {
      console.log(`🐝 Updating hive: ${docRef.id}`);
      batch.update(docRef.ref, { user_id: toUserId });
    });
    
    // Update inspections
    inspections.docs.forEach(docRef => {
      console.log(`📋 Updating inspection: ${docRef.id}`);
      batch.update(docRef.ref, { user_id: toUserId });
    });
    
    // Commit all updates
    await batch.commit();
    console.log('✅ All data updated successfully');
    
    // Delete the old user document
    await deleteDoc(doc(db, 'users', fromUserId));
    console.log('✅ Old user document deleted');
    
    return {
      success: true,
      message: `Successfully merged account data! ${apiaries.size} apiaries, ${hives.size} hives, and ${inspections.size} inspections have been transferred.`,
      mergedData: {
        apiaries: apiaries.size,
        hives: hives.size,
        inspections: inspections.size
      }
    };
    
  } catch (error) {
    console.error('❌ Error during account merge:', error);
    return {
      success: false,
      message: 'Failed to merge accounts',
      error: error instanceof Error ? error.message : 'Unknown error'
    };
  }
};

export const autoMergeCurrentUserAccount = async (): Promise<AccountMergeResult> => {
  const currentUser = auth.currentUser;
  if (!currentUser || !currentUser.email) {
    return {
      success: false,
      message: 'No authenticated user found',
      error: 'NO_AUTH_USER'
    };
  }
  
  console.log(`🔍 Looking for accounts to merge for email: ${currentUser.email}`);
  
  // Find all accounts with the same email
  const accountsWithSameEmail = await findAccountsWithSameEmail(currentUser.email);
  console.log(`Found ${accountsWithSameEmail.length} accounts with email ${currentUser.email}`);
  
  // Filter out the current user account
  const otherAccounts = accountsWithSameEmail.filter(account => account.uid !== currentUser.uid);
  
  if (otherAccounts.length === 0) {
    return {
      success: false,
      message: 'No other accounts found with the same email to merge',
      error: 'NO_ACCOUNTS_TO_MERGE'
    };
  }
  
  if (otherAccounts.length > 1) {
    console.warn('Multiple accounts found with same email. Using the first one.');
  }
  
  // Merge the first other account into the current account
  const sourceAccount = otherAccounts[0];
  console.log(`🔄 Merging account ${sourceAccount.uid} into current account ${currentUser.uid}`);
  
  return await mergeAccountData(sourceAccount.uid, currentUser.uid);
};